import {
  ForwardedRef,
  forwardRef,
  memo,
  useEffect,
  useRef,
  FocusEvent,
} from "react"

import { CellProps, useFocusManagement, useTree } from "components/TreeState"
import { ChangedPlanRecord, PlanRecord, PlanRecordType } from "../types"
import Input, { InputProps } from "ui/inputs/Input"
import { css, cx } from "@emotion/css"
import { formatFixedNumberCurrency, parseNumber } from "utils/number-format"
import { isSemanticReadonly, usePlanCachedData } from "../utils"
import { subscribe, useSnapshot } from "valtio"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { FieldsKey } from "../components/AutoCompute/types"
import { Info } from "./Info"
import { Tooltip } from "@material-ui/core"
import { format as currencyFormat } from "utils/currencies"
import { get } from "lodash"
import { set } from "lodash/fp"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useDefaultFieldId } from "../useDefaultFieldId"
import { useDefaultFieldIds } from "../useDefaultFieldIds"
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"
import { roundHalfEven } from "../components/AutoCompute/utils"

const fmt = (sum: number, currency?: string) => currencyFormat(sum, currency, 2)

export const PreciseCurrencyCell = memo(function _PreciseCurrencyCell(
  props: CellProps,
) {
  const precision = usePrecision()
  return <CurrencyCell {...props} precision={precision} />
})

export function CurrencyCell(props: CellProps) {
  const {
    rowId: id,
    dataKey: dk,
    columnId,
    disabled,
    primaryKey,
    semantic,
    precision = DEFAULT_PRECISION,
  } = props
  const { t } = useI18n()
  const defaultFieldIds = useDefaultFieldIds(id)
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const tree = useTree(tableSource)
  const aggregations = useAtomValue(tableAggregations)
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const defaultFieldId = useDefaultFieldId(id, semantic)
  const dataKey = dk || `${primaryKey}.${defaultFieldId}`
  const isBooking = primaryKey === "buyingMediaFields"
  const planData = usePlanCachedData(isBooking)

  const type = readFrom[id]?.type as PlanRecordType
  const packaged = readFrom[id]?.packaged
  const isMediaSubMedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const mediaFields = readFrom[id]?.[
    primaryKey as keyof PlanRecord
  ] as PlanRecord["planningMediaFields"]

  const readonly = isSemanticReadonly(
    type,
    primaryKey as FieldsKey,
    semantic,
    packaged,
    defaultFieldIds,
    mediaFields,
  )

  const hasSum =
    !packaged && !isBooking && semantic === "budget" && isMediaSubMedia

  const error = get(readError, `${id}.${columnId}`)
  const value = String(get(readFrom[id], dataKey) ?? "")
  const aggregatedValue =
    get(aggregations, [id, defaultFieldId || columnId]) ?? ""
  const hasAggregation = !!Object.keys(aggregations).includes(id)
  let appliedValue =
    isMediaSubMedia && !hasSum && hasAggregation && !isBooking && !packaged
      ? String(aggregatedValue ?? value)
      : value

  const forcedNumeric = !isNaN(Number(appliedValue))

  appliedValue = forcedNumeric
    ? roundHalfEven(Number(appliedValue), precision).toString()
    : appliedValue

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"

  const handleChange = (val: string) => {
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    writeTo[id] = set(dataKey, val, writeTo[id])
    tableSource.clearError(id)
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleBlur = () => {
    autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const { getInputProps } = useFocusManagement()

  const totalRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    if (totalRef.current === null) return
    const node = tree[id]
    const children = node?.children ?? []
    const getPlanned = () => {
      const sum = children.reduce((sum, curr) => {
        sum += Number(get(writeTo, `${curr}.${dataKey}`) || 0)
        return sum
      }, 0)
      if (totalRef.current) {
        totalRef.current.textContent = fmt(sum ?? "", planData?.currencyCode)
      }
    }
    getPlanned()

    const unsub = subscribe(writeTo, getPlanned)

    return () => unsub()
  }, [totalRef, writeTo, tree, id, dataKey, planData?.currencyCode])

  // apply billing type rules
  const viewable = useBillingTypesRules(
    readFrom[id] as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable) return <span />

  return (
    <div className={styles.wrapper}>
      <div>
        {!hasSum ? (
          <Bulky {...props} otherDisabled={!!readonly}>
            <ThisInputBelow
              {...getInputProps()}
              name={dataKey}
              value={appliedValue}
              setValue={handleChange}
              placeholder={disabled || readonly ? "" : "0.00"}
              selectOnFocus={false}
              disabled={disabled || readonly}
              currencyCode={planData?.currencyCode}
              onBlur={handleBlur}
              precision={precision}
            />
          </Bulky>
        ) : (
          <Bulky {...props} otherDisabled={!!readonly}>
            <div
              className={css`
                display: flex;
                flex-direction: column;
                width: 100%;
              `}
            >
              <ThisInputBelow
                {...getInputProps()}
                name={dataKey}
                value={value}
                setValue={handleChange}
                placeholder={disabled || readonly ? "" : "0.00"}
                selectOnFocus={false}
                disabled={disabled || readonly}
                currencyCode={planData?.currencyCode}
                onBlur={handleBlur}
                precision={precision}
              />
              <div
                className={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  width: 100%;
                `}
              >
                <span className="BodyBody-xsDark-greyRegular">
                  {t("planned")}
                </span>
                <span
                  ref={totalRef}
                  className="umbersNumber-2_rightDark-greyRegular"
                ></span>
              </div>
            </div>
          </Bulky>
        )}
        <Info {...props} />
        {!!error ? <CellError error={error} /> : null}
      </div>
    </div>
  )
}

type ThisInputBelowProps = InputProps & {
  name?: string
  value?: string
  setValue: (v: string) => void
  placeholder?: string
  selectOnFocus?: boolean
  currencyCode?: string
  precision: number
}

export const ThisInputBelow = forwardRef(
  (props: ThisInputBelowProps, ref: ForwardedRef<any>) => {
    const {
      name,
      value = "",
      setValue,
      selectOnFocus,
      onFocus: propOnFocus,
      onBlur,
      className,
      currencyCode,
      precision,
      ...rest
    } = props

    const rifm = useRifm({
      value: !!value ? `${value}${"0".repeat(precision)}` : "",
      accept: /[\d.]/g,
      onChange: (v) => setValue(parseNumber(v)),
      format: (v) => formatFixedNumberCurrency(v, currencyCode, precision),
    })

    const { handleFocus: autoFocus, hasChanged } = useAutocomputeValidation(
      rifm.value,
    )

    const handleFocus = (evt: FocusEvent<HTMLInputElement>) => {
      const _evt = evt
      if (selectOnFocus) {
        const inp = _evt.target
        inp && inp.setSelectionRange(0, inp?.value?.length || 0)
      }
      if (propOnFocus) {
        propOnFocus(_evt)
      }
      autoFocus()
    }

    const handleBlur = (evt: FocusEvent<HTMLInputElement>) => {
      !!onBlur && hasChanged(rifm.value) && onBlur(evt)
    }

    return (
      <Tooltip title={rifm.value} placement="bottom-end">
        <Input
          {...rest}
          ref={ref}
          autoComplete="off"
          type="tel"
          name={name}
          value={rifm.value}
          onChange={rifm.onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cx(styles.input, className)}
        />
      </Tooltip>
    )
  },
)

const styles = {
  wrapper: css`
    width: 100%;
  `,
  input: css`
    text-align: right;
    font-size: 13px;
    font-weight: inherit;
  `,
}
